/** @jsxImportSource @emotion/react */
import { MoreOutlined } from "@ant-design/icons";
import { css } from "@emotion/react";
import { Button, Dropdown, Form, Input, Menu, Table } from "antd";
import { TablePaginationConfig } from "antd/lib/table";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import UpdatePassword from "src/containers/userManage/components/UpdatePassword";
import UserDetail from "src/containers/userManage/components/UserDetail";
import { useFormatMessage } from "src/hooks";
import messageIds from "src/locales/messageIds";
import { UserList } from "src/models/userManage";
import { UserSeviceClient } from "src/services/UserManagementService";

export default React.memo(function UserManage() {
  const formatMessage = useFormatMessage();
  const [dataSource, setDataSource] = useState<UserList[]>([]);
  const [pagination, setPagiation] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  } as TablePaginationConfig);
  const [userName, setUserName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [isView, setIsView] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isShow, setIsShow] = useState(false);
  const [userId, setUserId] = useState("");

  let isUnmounted = false;
  const list = useCallback(
    (
      pageIndex?: number,
      pageSize?: number,
      userName?: string,
      phoneNumber?: string,
      email?: string
    ) => {
      UserSeviceClient.getUsers(
        pageIndex,
        pageSize,
        userName,
        phoneNumber,
        email
      ).then((res) => {
        if (!isUnmounted) {
          setDataSource(res.data.list);
          setPagiation({
            ...pagination,
            total: res.data.count,
          });
          setLoading(false);
        }
      });
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isUnmounted, pagination.current, pagination.pageSize]
  );

  const onView = useCallback((id) => {
    setIsView(true);
    setUserId(id);
  }, []);

  const onClose = useCallback(() => {
    setIsView(false);
  }, []);

  const showModal = useCallback((userId) => {
    setIsShow(true);
    setUserId(userId);
  }, []);

  const onShowClose = useCallback(() => {
    setIsShow(false);
  }, []);

  const menu = useCallback(
    (record: UserList) => (
      <Menu>
        <Menu.Item
          key="view"
          css={css`
            font-size: 12px;
            :hover {
              color: rgb(51, 119, 255);
              border-radius: 4px;
              cursor: pointer;
              background: rgb(51, 119, 255, 0.1);
            }
          `}
          onClick={() => onView(record?.userId)}
        >
          {formatMessage(messageIds.common.view)}
        </Menu.Item>
        <Menu.Item
          key="updatePassword"
          css={css`
            font-size: 12px;
            :hover {
              color: rgb(51, 119, 255);
              border-radius: 4px;
              cursor: pointer;
              background: rgb(51, 119, 255, 0.1);
            }
          `}
          onClick={() => showModal(record?.userId)}
        >
          {formatMessage(messageIds.userManagement.updatePassword)}
        </Menu.Item>
      </Menu>
    ),
    [formatMessage, onView, showModal]
  );

  const columns = useMemo(
    () => [
      {
        title: formatMessage(messageIds.userManagement.columns.userName),
        dataIndex: "userName",
      },
      {
        title: formatMessage(messageIds.userManagement.columns.phoneNumber),
        dataIndex: "phoneNumber",
      },
      {
        title: formatMessage(messageIds.userManagement.columns.email),
        dataIndex: "email",
      },
      {
        title: formatMessage(messageIds.userManagement.columns.lastLoginedAt),
        dataIndex: "lastLoginedAt",
        render: (text: string, record: UserList) => (
          <span>
            {record?.lastLoginedAt &&
              moment(record?.lastLoginedAt).format("YYYY-MM-DD HH:mm:ss")}
          </span>
        ),
      },
      {
        title: formatMessage(messageIds.userManagement.columns.lastModifiedAt),
        dataIndex: "lastModifiedAt",
        render: (text: string, record: UserList) => (
          <span>
            {record?.lastModifiedAt &&
              moment(record?.lastModifiedAt).format("YYYY-MM-DD HH:mm:ss")}
          </span>
        ),
      },
      {
        title: formatMessage(messageIds.common.operation),
        dataIndex: "operation",
        render: (text: string, record: UserList) => (
          <Dropdown
            overlay={menu(record)}
            placement="bottomLeft"
            css={css`
              display: flex;
              align-items: center;
              justify-content: center;
              width: 30px;
              height: 30px;
              :hover {
                color: rgb(51, 119, 255);
                border-radius: 4px;
                cursor: pointer;
                background: rgb(51, 119, 255, 0.1);
              }
            `}
          >
            <MoreOutlined />
          </Dropdown>
        ),
      },
    ],
    [formatMessage, menu]
  );

  const onSearch = useCallback(() => {
    const current = Number(pagination.current) - 1;
    list(current, pagination.pageSize, userName, phoneNumber, email);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    email,
    list,
    pagination.current,
    pagination.pageSize,
    phoneNumber,
    userName,
  ]);

  useEffect(() => {
    setLoading(true);
    const current = Number(pagination.current) - 1;
    list(current, pagination.pageSize, userName, phoneNumber, email);

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      isUnmounted = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, pagination.current, pagination.pageSize]);

  const onTableChange = useCallback(
    (tablePagination: TablePaginationConfig) => {
      setPagiation({
        ...pagination,
        current: tablePagination.current,
        pageSize: tablePagination.pageSize,
      });
    },
    [pagination]
  );
  return (
    <div
      css={css`
        flex-grow: 1;
        padding: 30px;
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          margin-right: 30px;
          padding-bottom: 30px;
        `}
      >
        <div
          css={css`
            max-width: 1000px;
            margin-right: 20px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          `}
        >
          <h1
            css={css`
              position: relative;
              margin: 0px 0px 8px;
              padding-left: 10px;
              line-height: 18px;
              font-size: 18px;
              font-weight: 600;
              color: rgb(61, 73, 102);
              ::before {
                content: "";
                position: absolute;
                left: 0px;
                width: 3px;
                height: 18px;
                background: rgb(51, 119, 255);
              }
            `}
          >
            {formatMessage(messageIds.menu.userManagement.name)}
          </h1>
        </div>
      </div>
      <div
        css={css`
          display: flex;
          padding-bottom: 24px;
          border-bottom: 1px solid rgb(202, 209, 224);
        `}
      >
        <Form layout="inline">
          <Form.Item
            label={formatMessage(messageIds.userManagement.columns.userName)}
          >
            <Input
              placeholder={formatMessage(
                messageIds.userManagement.searchPlaceholder.userName
              )}
              onChange={(e) => {
                setPagiation({
                  ...pagination,
                  current: 1,
                });
                setUserName(e.target.value);
              }}
              allowClear
            ></Input>
          </Form.Item>
          <Form.Item
            label={formatMessage(messageIds.userManagement.columns.email)}
          >
            <Input
              placeholder={formatMessage(
                messageIds.userManagement.searchPlaceholder.email
              )}
              onChange={(e) => {
                setPagiation({
                  ...pagination,
                  current: 1,
                });
                setEmail(e.target.value);
              }}
              allowClear
            ></Input>
          </Form.Item>
          <Form.Item
            label={formatMessage(messageIds.userManagement.columns.phoneNumber)}
          >
            <Input
              placeholder={formatMessage(
                messageIds.userManagement.searchPlaceholder.phoneNumber
              )}
              onChange={(e) => {
                setPagiation({
                  ...pagination,
                  current: 1,
                });
                setPhoneNumber(e.target.value);
              }}
              allowClear
            ></Input>
          </Form.Item>
          <Form.Item>
            <Button type="primary" onClick={onSearch}>
              {formatMessage(messageIds.common.select)}
            </Button>
          </Form.Item>
        </Form>
      </div>
      <div
        css={css`
          margin-top: 30px;
        `}
      >
        <Table
          columns={columns}
          dataSource={dataSource}
          rowKey={(record) => record?.id}
          onChange={(pagination) => onTableChange(pagination)}
          pagination={{
            ...pagination,
            showSizeChanger: true,
          }}
          loading={loading}
        ></Table>
      </div>
      <UserDetail
        isView={isView}
        userId={userId}
        onClose={onClose}
      ></UserDetail>
      <UpdatePassword
        isShow={isShow}
        onShowClose={onShowClose}
        userId={userId}
        list={onSearch}
      ></UpdatePassword>
    </div>
  );
});
